import { useMemo, useState } from "react";
import { ArrowDownRight, Calculator } from "lucide-react";
import { Reveal, SectionHead } from "./shared";

const CATALOG = {
  "Home Remodels": { unit: "sq ft", label: "Remodel area", min: 85, max: 180, def: 300, step: 10, smin: 50, smax: 3000 },
  "Custom Decks": { unit: "sq ft", label: "Deck size", min: 45, max: 110, def: 250, step: 10, smin: 50, smax: 1500 },
  "Room Additions": { unit: "sq ft", label: "Addition size", min: 150, max: 300, def: 240, step: 10, smin: 50, smax: 1500 },
  "Fencing": { unit: "lin ft", label: "Fence length", min: 28, max: 60, def: 160, step: 5, smin: 40, smax: 800 },
  "Roofing": { unit: "sq ft", label: "Roof area", min: 6, max: 14, def: 1800, step: 50, smin: 50, smax: 6000 },
  "Handyman Services": { unit: "hours", label: "Estimated hours", min: 60, max: 110, def: 4, step: 1, smin: 1, smax: 40 },
};

const TIERS = [
  { id: "standard", label: "Standard", mult: 1 },
  { id: "premium", label: "Premium", mult: 1.35 },
];

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function Estimator() {
  const services = Object.keys(CATALOG);
  const [service, setService] = useState(services[0]);
  const [qty, setQty] = useState(CATALOG[services[0]].def);
  const [tier, setTier] = useState("standard");

  const catalog = CATALOG[service];
  const range = useMemo(() => {
    const m = TIERS.find((t) => t.id === tier).mult;
    return { low: catalog.min * qty * m, high: catalog.max * qty * m };
  }, [catalog, qty, tier]);

  const onServiceChange = (s) => {
    setService(s);
    setQty(CATALOG[s].def);
  };

  return (
    <section data-testid="estimator-section" className="py-20 md:py-32">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <SectionHead
            eyebrow="Ballpark in 20 seconds"
            title="What might my project cost?"
            sub="Pick a project, drag the size, choose a finish level. You'll get a realistic ballpark range — the same numbers we start from when we write your estimate."
          />
          <Reveal delay={0.2}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel mt-8 border-l-2 border-safety pl-4 leading-loose">
              Ballpark only — final pricing comes after a free site visit
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div data-testid="estimator-panel" className="bg-ink-card border border-line clip-notch p-6 sm:p-10">
              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-safety font-bold">
                <Calculator className="w-4 h-4" strokeWidth={2.5} /> Project Estimator
              </div>

              <div className="mt-8">
                <label htmlFor="est-service" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-3">
                  1 — Project type
                </label>
                <div className="flex flex-wrap gap-2">
                  {services.map((s) => (
                    <button
                      key={s}
                      onClick={() => onServiceChange(s)}
                      data-testid={`estimator-service-${s.toLowerCase().replace(/ /g, "-")}`}
                      className={`font-cond font-semibold uppercase tracking-wide text-sm px-4 py-2.5 border transition-all duration-200 ${
                        service === s ? "border-safety bg-safety text-ink" : "border-line text-steel hover:border-steel hover:text-bone"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <label htmlFor="est-qty" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-3">
                  2 — {catalog.label} ({catalog.unit})
                </label>
                <input
                  id="est-qty"
                  type="range"
                  min={catalog.smin}
                  max={catalog.smax}
                  step={catalog.step}
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                  data-testid="estimator-qty-range"
                  className="w-full accent-[#CC5500] cursor-pointer"
                />
                <div className="flex items-baseline gap-2 mt-3">
                  <span className="font-display text-4xl text-bone" data-testid="estimator-qty-value">{qty.toLocaleString()}</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-steel">{catalog.unit}</span>
                </div>
              </div>

              <div className="mt-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-3">3 — Finish level</span>
                <div className="grid grid-cols-2 gap-2">
                  {TIERS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTier(t.id)}
                      data-testid={`estimator-tier-${t.id}`}
                      className={`font-cond font-semibold uppercase tracking-wide text-sm px-4 py-2.5 border transition-all duration-200 ${
                        tier === t.id ? "border-safety bg-safety text-ink" : "border-line text-steel hover:border-steel hover:text-bone"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-10 border-t border-line pt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel">Your ballpark range</p>
                  <p className="font-display text-4xl sm:text-5xl text-safety mt-2 leading-none" data-testid="estimator-range">
                    {fmt(range.low)} – {fmt(range.high)}
                  </p>
                </div>
                <a
                  href="#quote"
                  data-testid="estimator-quote-cta"
                  className="inline-flex items-center justify-center gap-2 bg-bone text-ink font-bold text-sm px-6 py-3.5 clip-notch-sm hover:bg-safety transition-all duration-200 hover:-translate-y-1 shrink-0"
                >
                  Lock in a real quote <ArrowDownRight className="w-4 h-4" strokeWidth={2.5} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
